import React, { useState, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  CloudUpload as UploadIcon,
  AutoAwesome as AutoIcon,
  // Popular icons
  Dashboard,
  Home,
  Settings,
  Person,
  Work,
  School,
  ShoppingCart,
  Restaurant,
  LocalMovies,
  MusicNote,
  SportsEsports,
  FitnessCenter,
  Flight,
  DirectionsCar,
  Computer,
  Phone,
  Email,
  Chat,
  Favorite,
  Star,
  Bookmark,
  Link,
  Folder,
  CloudDownload,
  CloudUpload,
  Security,
  Lock,
  Key,
  Visibility,
  Edit,
  Delete,
  Add,
  Remove,
  Save,
  Print,
  Share,
  Download,
  Upload,
  Refresh,
  Search,
  FilterList,
  Sort,
  ViewList,
  ViewModule,
  GridView,
  Map,
  Timeline,
  BarChart,
  PieChart,
  TrendingUp,
  AccountBalance,
  Payment,
  LocalOffer,
  ShoppingBag,
  Store,
  Business,
  Group,
  Public,
  Language,
  Translate,
  Schedule,
  Today,
  Event,
  Alarm,
  Timer,
  History,
  Update,
  Sync,
  Build,
  BugReport,
  Psychology,
  SmartToy,
  Science,
  Biotech,
  Code,
  Memory,
  Storage,
  Speed,
  Power,
  BatteryFull,
  Wifi,
  Bluetooth,
  Usb,
  Mouse,
  Keyboard,
  Monitor,
  Tablet,
  Watch,
  Headphones,
  Camera,
  Photo,
  Videocam,
  Mic,
  VolumeUp
} from '@mui/icons-material';
import { serviceLogos, detectServiceLogo, checkImageAccessibility } from '../../utils/logoDetection';
import ThinkBeyondLogo from '../../assets/images/thumbnail_Think Beyond AI-favicon.jpg';

interface IconPickerProps {
  open: boolean;
  onClose: () => void;
  onIconSelect: (iconName: string, iconType?: 'material' | 'service' | 'custom', iconUrl?: string) => void;
  selectedIcon?: string;
  selectedIconType?: 'material' | 'service' | 'custom';
  selectedIconUrl?: string;
  tileUrl?: string;
  tileTitle?: string;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Icon categories with their icons
const iconCategories = {
  suggested: {
    name: 'Suggested',
    icons: {} // Will be populated dynamically
  },
  upload: {
    name: 'Custom Upload',
    icons: {}
  },
  popular: {
    name: 'Popular',
    icons: {
      thinkBeyond: () => (
        <Avatar
          src={ThinkBeyondLogo}
          sx={{ width: 32, height: 32 }}
          alt="ThinkBeyond"
        >
          TB
        </Avatar>
      ),
      dashboard: Dashboard,
      home: Home,
      settings: Settings,
      person: Person,
      work: Work,
      school: School,
      shoppingCart: ShoppingCart,
      restaurant: Restaurant,
      localMovies: LocalMovies,
      musicNote: MusicNote,
      sportsEsports: SportsEsports,
      fitnessCenter: FitnessCenter,
      flight: Flight,
      directionsCar: DirectionsCar,
      computer: Computer,
      phone: Phone,
      email: Email,
      chat: Chat,
      favorite: Favorite,
      star: Star,
      bookmark: Bookmark,
      link: Link,
      folder: Folder
    }
  },
  actions: {
    name: 'Actions',
    icons: {
      add: Add,
      remove: Remove,
      edit: Edit,
      delete: Delete,
      save: Save,
      print: Print,
      share: Share,
      download: Download,
      upload: Upload,
      cloudDownload: CloudDownload,
      cloudUpload: CloudUpload,
      refresh: Refresh,
      search: Search,
      filterList: FilterList,
      sort: Sort,
      check: CheckIcon,
      close: CloseIcon
    }
  },
  business: {
    name: 'Business',
    icons: {
      business: Business,
      accountBalance: AccountBalance,
      payment: Payment,
      localOffer: LocalOffer,
      shoppingBag: ShoppingBag,
      store: Store,
      group: Group,
      barChart: BarChart,
      pieChart: PieChart,
      trendingUp: TrendingUp,
      timeline: Timeline,
      work: Work
    }
  },
  communication: {
    name: 'Communication',
    icons: {
      email: Email,
      phone: Phone,
      chat: Chat,
      public: Public,
      language: Language,
      translate: Translate,
      wifi: Wifi,
      bluetooth: Bluetooth
    }
  },
  time: {
    name: 'Time & Schedule',
    icons: {
      schedule: Schedule,
      today: Today,
      event: Event,
      alarm: Alarm,
      timer: Timer,
      history: History,
      update: Update,
      sync: Sync
    }
  },
  tech: {
    name: 'Technology',
    icons: {
      computer: Computer,
      tablet: Tablet,
      phone: Phone,
      watch: Watch,
      memory: Memory,
      storage: Storage,
      speed: Speed,
      power: Power,
      batteryFull: BatteryFull,
      usb: Usb,
      mouse: Mouse,
      keyboard: Keyboard,
      monitor: Monitor,
      code: Code,
      bugReport: BugReport,
      build: Build,
      psychology: Psychology,
      smartToy: SmartToy,
      science: Science,
      biotech: Biotech
    }
  },
  media: {
    name: 'Media',
    icons: {
      photo: Photo,
      camera: Camera,
      videocam: Videocam,
      musicNote: MusicNote,
      headphones: Headphones,
      mic: Mic,
      volumeUp: VolumeUp,
      localMovies: LocalMovies
    }
  },
  security: {
    name: 'Security',
    icons: {
      security: Security,
      lock: Lock,
      key: Key,
      visibility: Visibility
    }
  },
  views: {
    name: 'Views',
    icons: {
      viewList: ViewList,
      viewModule: ViewModule,
      gridView: GridView,
      map: Map
    }
  }
};

const IconPicker: React.FC<IconPickerProps> = ({
  open,
  onClose,
  onIconSelect,
  selectedIcon = 'dashboard',
  selectedIconType = 'material',
  selectedIconUrl,
  tileUrl,
  tileTitle
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(0);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Detect suggested logos based on tile URL/title
  const suggestedLogos = useMemo(() => {
    if (!tileUrl && !tileTitle) return {};
    
    const suggestions: Record<string, any> = {};
    
    // Try to detect service
    const detectedService = detectServiceLogo(tileUrl || '', tileTitle);
    if (detectedService) {
      suggestions[detectedService.name.toLowerCase().replace(/\s+/g, '')] = () => (
        <Avatar
          src={detectedService.logoUrl}
          sx={{ width: 32, height: 32 }}
          alt={detectedService.name}
        >
          {detectedService.name[0]}
        </Avatar>
      );
    }
    
    return suggestions;
  }, [tileUrl, tileTitle]);
  
  // Update suggested category with detected logos
  if (Object.keys(suggestedLogos).length > 0) {
    iconCategories.suggested.icons = suggestedLogos;
  }

  const categories = Object.keys(iconCategories).filter(key => {
    // Show suggested tab only if we have suggestions
    if (key === 'suggested') {
      return Object.keys(suggestedLogos).length > 0;
    }
    return true;
  });
  
  const currentCategory = categories[activeCategory];
  const currentIcons = iconCategories[currentCategory as keyof typeof iconCategories]?.icons || {};

  // Filter icons based on search query
  const filteredIcons = useMemo(() => {
    if (!searchQuery) return currentIcons;
    
    const query = searchQuery.toLowerCase();
    const filtered: Record<string, any> = {};
    
    Object.entries(currentIcons).forEach(([name, component]) => {
      if (name.toLowerCase().includes(query)) {
        filtered[name] = component;
      }
    });
    
    return filtered;
  }, [currentIcons, searchQuery]);

  const handleIconSelect = (iconName: string, iconType: 'material' | 'service' | 'custom' = 'material', iconUrl?: string) => {
    // Handle ThinkBeyond favicon specially
    if (iconName === 'thinkBeyond') {
      onIconSelect(iconName, 'custom', ThinkBeyondLogo);
    } else {
      onIconSelect(iconName, iconType, iconUrl);
    }
    onClose();
  };
  
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size must be less than 5MB');
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setUploadedImage(dataUrl);
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadError('Failed to upload image');
      setIsUploading(false);
    }
  };
  
  const handleUseUploadedImage = () => {
    if (uploadedImage) {
      handleIconSelect('custom-upload', 'custom', uploadedImage);
    }
  };
  
  const handleUseSuggestedLogo = (logoName: string) => {
    const detectedService = detectServiceLogo(tileUrl || '', tileTitle);
    if (detectedService) {
      handleIconSelect(logoName, 'service', detectedService.logoUrl);
    }
  };

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveCategory(newValue);
    setSearchQuery(''); // Clear search when changing category
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: 600 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Select Icon</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Search */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            placeholder="Search icons..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Box>

        {/* Categories */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {categories.map((categoryKey, index) => {
              const category = iconCategories[categoryKey as keyof typeof iconCategories];
              return (
                <Tab
                  key={categoryKey}
                  label={category.name}
                  id={`icon-category-${index}`}
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Selected Icon Preview */}
        {selectedIcon && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'action.hover' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Current selection:
              </Typography>
              <Chip
                label={selectedIcon}
                color="primary"
                icon={React.createElement(
                  (currentIcons as any)[selectedIcon] || Dashboard
                )}
              />
            </Box>
          </Box>
        )}

        {/* Icons Grid */}
        <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
          {/* Suggested Logos Tab */}
          {currentCategory === 'suggested' && Object.keys(suggestedLogos).length > 0 && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" icon={<AutoIcon />}>
                  We found logos that might match your tile based on the URL or title.
                </Alert>
              </Box>
              <Grid container spacing={2}>
                {Object.entries(suggestedLogos).map(([logoName, LogoComponent]) => {
                  const detectedService = detectServiceLogo(tileUrl || '', tileTitle);
                  return (
                    <Grid item key={logoName}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          cursor: 'pointer',
                          minWidth: 100,
                          minHeight: 100,
                          border: 1,
                          borderColor: 'divider',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'primary.50',
                            transform: 'translateY(-2px)',
                            boxShadow: 2,
                          },
                        }}
                        onClick={() => handleUseSuggestedLogo(logoName)}
                      >
                        <LogoComponent />
                        <Typography
                          variant="caption"
                          align="center"
                          sx={{ mt: 1, fontWeight: 500 }}
                        >
                          {detectedService?.name || logoName}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
          
          {/* Custom Upload Tab */}
          {currentCategory === 'upload' && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Alert severity="info">
                  Upload your own image to use as an icon. Supported formats: PNG, JPG, SVG (max 5MB)
                </Alert>
              </Box>
              
              {/* Upload Button */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={isUploading ? <CircularProgress size={20} /> : <UploadIcon />}
                  disabled={isUploading}
                  sx={{ mb: 2 }}
                >
                  {isUploading ? 'Uploading...' : 'Choose Image'}
                  <VisuallyHiddenInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                  />
                </Button>
                
                {uploadError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {uploadError}
                  </Alert>
                )}
              </Box>
              
              {/* Preview Uploaded Image */}
              {uploadedImage && (
                <Box sx={{ textAlign: 'center' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Avatar
                      src={uploadedImage}
                      sx={{ width: 64, height: 64, mb: 2 }}
                      variant="rounded"
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleUseUploadedImage}
                    >
                      Use This Image
                    </Button>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
          
          {/* Material Icons */}
          {currentCategory !== 'suggested' && currentCategory !== 'upload' && (
            Object.keys(filteredIcons).length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No icons found for "{searchQuery}"
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={1}>
                {Object.entries(filteredIcons).map(([iconName, IconComponent]) => (
                  <Grid item key={iconName}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        minWidth: 80,
                        minHeight: 80,
                        border: 1,
                        borderColor: selectedIcon === iconName && (selectedIconType === 'material' || (iconName === 'thinkBeyond' && selectedIconType === 'custom')) ? 'primary.main' : 'divider',
                        backgroundColor: selectedIcon === iconName && (selectedIconType === 'material' || (iconName === 'thinkBeyond' && selectedIconType === 'custom')) ? 'primary.50' : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'primary.50',
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleIconSelect(iconName)}
                    >
                      {React.createElement(IconComponent as React.ComponentType<any>, { sx: { fontSize: 32, mb: 0.5 } })}
                      <Typography
                        variant="caption"
                        align="center"
                        sx={{
                          fontSize: '0.7rem',
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                        }}
                      >
                        {iconName}
                      </Typography>
                      {selectedIcon === iconName && (selectedIconType === 'material' || (iconName === 'thinkBeyond' && selectedIconType === 'custom')) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'primary.main',
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 12, color: 'white' }} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IconPicker;